import { observer } from 'mobx-react-lite';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { AriaRoles } from 'owa-accessibility';
import { logUsage } from 'owa-analytics';
import { AttachmentOpenAction, getAttachmentDefaultOpenAction } from 'owa-attachment-data';
import {
    ActionType,
    AttachmentFullViewState,
    cancelConvertClassicToCloudy,
    cancelConvertCloudyToClassic,
    setAttachmentShouldShowContextMenu,
} from 'owa-attachment-full-data';
import { AttachmentModel } from 'owa-attachment-model-store';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import {
    lazyClickAttachment,
    PreviewContext,
    AttachmentSelectionSource,
} from 'owa-attachment-select-actions';
import { AttachmentDragData } from 'owa-attachment-drag-and-drop';
import { ClientItemId } from 'owa-client-ids';
import Draggable from 'owa-dnd/lib/components/Draggable';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isLightningItemUnseen, LightningId } from 'owa-lightning-v2';
import loc from 'owa-localize';
import { SavingLabel } from 'owa-locstrings/lib/strings/savinglabel.locstring.json';
import { InstrumentationContext } from 'owa-search/lib/types/InstrumentationContext';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import { AttachmentPreviewMode } from 'owa-sxs-store';
import { ImageGalleryItem } from 'owa-sxsdata';
import { trace } from 'owa-trace';
import * as React from 'react';
import datapoints from '../datapoints';
import { EditingLabel } from '../strings.locstring.json';
import { SurfaceAction } from '../types/SurfaceAction';
import {
    handleComponentOnBlur,
    handleComponentOnFocus,
    handleOnMouseEnter,
    handleOnMouseLeave,
} from '../utils/handleAttachmentViewFocus';
import AttachmentDocView from './AttachmentDocView';
import AttachmentImageView from './AttachmentImageView';
import AttachmentProgressIndicator from './AttachmentProgressIndicator';
import { openAriaLabel } from './AttachmentView.locstring.json';
import AttachmentContextMenuView from './ContextMenu/AttachmentContextMenuView';
import { getShouldHideSurfaceActionButton } from './SurfaceActions/getShouldHideSurfaceActionButton';
import ProjectionContext from 'owa-popout-v2/lib/context/ProjectionContext';
import { isOfficeFile } from '../utils/isOfficeFile';

export interface AttachmentProps {
    attachmentViewState: AttachmentFullViewState;
    onDeleteButtonClicked: (attachmentViewState: AttachmentFullViewState) => void;
    onReplaceAttachment: (
        attachmentViewState: AttachmentFullViewState,
        attachmentType: AttachmentType,
        isReadOnly: boolean,
        uploadCompleted: boolean
    ) => void;
    onDragStart: (attachment: AttachmentType) => AttachmentDragData;
    instrumentationContext: InstrumentationContext;
    onBeforePreview?: () => void;
    isImage: boolean;
    previewMode: AttachmentPreviewMode;
    parentItemId: ClientItemId;
    isDraft: boolean;
    attachment?: AttachmentModel;
    moveAttachmentToInlineHandler?: (
        attachment: AttachmentFullViewState,
        contentId: string
    ) => void;
    createImageGallery: () => Promise<ImageGalleryItem[]>;
    registerFocusFunction: (attachmentId: string, focusFunction: () => void) => void;
    referenceAttachmentsSupported?: () => boolean;
    isSxS: boolean;
}

const styles = require('./AttachmentView.scss');
const classNames = require('classnames/bind').bind(styles);

const DRAG_X_OFFSET = -12;
const DRAG_Y_OFFSET = 5;

// This component is responsible for showing the attachment view.
export default observer(function AttachmentView(props: AttachmentProps) {
    props = {
        attachment: getAttachment(props.attachmentViewState.attachmentId),
        ...props,
    };

    React.useEffect(() => {
        if (props.registerFocusFunction) {
            props.registerFocusFunction(props.attachmentViewState.attachmentId.Id, focus);
        }
    }, [props.registerFocusFunction]);

    React.useEffect(() => {
        if (props.previewMode === AttachmentPreviewMode.PendingNavigation) {
            performOpenAction();
        }
    }, [props.previewMode]);

    const attachmentDiv = React.useRef<HTMLElement>();
    const defaultOpenAction = React.useRef<AttachmentOpenAction>();
    const targetWindow = React.useContext(ProjectionContext);
    const renderAttachmentView = () => {
        const previewContext: PreviewContext = {
            viewState: props.attachmentViewState,
            attachmentSelectionSource: AttachmentSelectionSource.ReadingPane,
            parentItemId: props.parentItemId,
            isDraft: props.isDraft,
            isEdit: false,
            onBeforePreview: props.onBeforePreview,
            createImageGallery: props.createImageGallery,
            instrumentationContext: props.instrumentationContext,
            targetWindow: targetWindow,
        };
        const shouldShowOpenAndEditInCloudFRECallout = shouldShowActionsContainerForOpenAndEditInCloudFRECallout(
            props.attachmentViewState
        );
        const isActionsContainerVisible =
            getIsSelected(props.previewMode) ||
            props.attachmentViewState.shouldShowActionsContainer ||
            shouldShowOpenAndEditInCloudFRECallout;
        return [
            props.isImage ? (
                <AttachmentImageView
                    key="view"
                    attachmentViewState={props.attachmentViewState}
                    attachment={props.attachment}
                    onDeleteButtonClicked={onDeleteButtonClicked}
                    isActionsContainerVisible={isActionsContainerVisible}
                    parentItemId={props.parentItemId}
                />
            ) : (
                <AttachmentDocView
                    key="view"
                    attachmentViewState={props.attachmentViewState}
                    attachment={props.attachment}
                    onDeleteButtonClicked={onDeleteButtonClicked}
                    previewState={props.previewMode}
                    isActionsContainerVisible={isActionsContainerVisible}
                    parentItemId={props.parentItemId}
                />
            ),
            <AttachmentProgressIndicator
                attachmentViewState={props.attachmentViewState}
                key="progress"
            />,
            <AttachmentContextMenuView
                key="contextMenu"
                attachment={props.attachment}
                attachmentViewState={props.attachmentViewState}
                targetElementGetter={attachmentDivGetter}
                previewContext={previewContext}
                parentItemId={props.parentItemId}
                onReplaceAttachment={props.onReplaceAttachment}
                moveAttachmentToInlineHandler={props.moveAttachmentToInlineHandler}
                referenceAttachmentsSupported={props.referenceAttachmentsSupported}
                isSxS={props.isSxS}
            />,
        ];
    };
    const handleOnFocus = () => {
        handleComponentOnFocus(props.attachmentViewState, props.isImage);
    };
    const handleOnBlur = () => {
        handleComponentOnBlur(props.attachmentViewState, props.isImage);
    };
    const handleMainContainerMouseOver = () => {
        handleOnMouseEnter(props.attachmentViewState, props.isImage);
    };
    const handleMainContainerMouseLeave = () => {
        handleOnMouseLeave(props.attachmentViewState, props.isImage);
    };
    const attachmentDivGetter = (): HTMLElement => {
        return attachmentDiv.current;
    };
    const keyDown = (event: React.KeyboardEvent<unknown>) => {
        if (event.keyCode === KeyCodes.enter || event.keyCode === KeyCodes.space) {
            trace.info('AttachmentView.keyPressEnter');
            performOpenAction();
        }
    };
    const performOpenAction = () => {
        if (
            props.previewMode === AttachmentPreviewMode.None ||
            props.previewMode === AttachmentPreviewMode.PendingNavigation
        ) {
            if (defaultOpenAction.current !== AttachmentOpenAction.NoAction) {
                const { name: eventName, customData: customData } = datapoints.OnClick;
                logUsage(eventName, customData(defaultOpenAction.current));
                setAttachmentShouldShowContextMenu(props.attachmentViewState, false);
                lazyClickAttachment(
                    props.attachmentViewState,
                    AttachmentSelectionSource.ReadingPane,
                    props.instrumentationContext,
                    props.parentItemId,
                    props.isDraft,
                    props.onBeforePreview,
                    props.createImageGallery,
                    null,
                    targetWindow
                );
            }
        }
    };
    const onContextMenu = (evt: React.MouseEvent<unknown>) => {
        evt.preventDefault();
        setAttachmentShouldShowContextMenu(props.attachmentViewState, true);
    };
    const onDeleteButtonClicked = (attachmentViewState: AttachmentFullViewState) => {
        switch (attachmentViewState.ongoingAction) {
            case ActionType.ConvertClassicToCloudy:
                try {
                    cancelConvertClassicToCloudy(attachmentViewState);
                } catch (error) {
                    // The above action throws to end datapoint with error. So just ignore the error here.
                }
                break;
            case ActionType.ConvertCloudyToClassic:
                try {
                    cancelConvertCloudyToClassic(attachmentViewState);
                } catch (error) {
                    // The above action throws to end datapoint with error. So just ignore the error here.
                }
                break;
            default:
                props.onDeleteButtonClicked(attachmentViewState);
        }
    };
    const canDrag = (): boolean => {
        return !!props.attachment;
    };
    const getDragData = () => {
        return props.onDragStart(props.attachment.model);
    };
    const getDragPreview = (): HTMLElement => {
        const elem = document.createElement('div');
        elem.className = styles.dragPreview;
        elem.innerText = props.attachment.model.Name;
        return elem;
    };
    const focus = () => {
        if (attachmentDiv.current) {
            attachmentDiv.current.focus();
        }
    };
    if (!props.attachment) {
        if (props.isDraft) {
            // The reason we return a null in case the AttachmentModel is not available and it is draft is because
            // when user forwards an email with attachment and then delete attachment (which deletes the model)
            // and then tries sending it, compose keeps an older draft which still has the same attachment but
            // the corresponding model was deleted causing this view to not have the AttachmentModel in the props.
            // Rendering older draft even though a mail was forwarded is by design from compose and hence we need
            // to handle this situation so that the view does not break.
            return null;
        }
        // If we are not in a draft we do not expect this to happen. Currently this is throwing a component error that
        // is crashing the app. Replacing with trace.error so that we can still track the issue, but minimize user impact.
        const { isCloudy, fileType } = props.attachmentViewState;
        trace.warn(
            `Attachment not found for AttachmentView. IsCloudy: ${isCloudy}, fileType: ${fileType}`
        );
        logUsage('AttachmentViewNotFound', {
            isCloudy,
            fileType,
        });
        return null;
    }
    let attachmentLabelText: String;
    switch (props.previewMode) {
        case AttachmentPreviewMode.Edit:
            attachmentLabelText = loc(EditingLabel);
            break;
        case AttachmentPreviewMode.SelectedAndSaving:
        case AttachmentPreviewMode.NotSelectedAndSaving:
            attachmentLabelText = loc(SavingLabel);
            break;
        default:
            attachmentLabelText = '';
    }
    const showAttachmentLabel = attachmentLabelText !== '';
    defaultOpenAction.current = getAttachmentDefaultOpenAction(props.attachmentViewState.strategy);
    const conditionalStyles = {
        attachmentImageView: props.isImage,
        attachmentDocView: !props.isImage,
        handCursor: defaultOpenAction.current !== AttachmentOpenAction.NoAction,
        defaultCursor: defaultOpenAction.current === AttachmentOpenAction.NoAction,
        regularTopBorderRadius: showAttachmentLabel,
    };
    conditionalStyles[getAttachmentStyle(props.previewMode)] = true;
    return (
        <div className={styles.attachmentContainer}>
            <div
                role={AriaRoles.option}
                onKeyDown={keyDown}
                aria-label={props.attachment.model.Name + ' ' + loc(openAriaLabel)}
                tabIndex={0}
                className={classNames(styles.attachmentView, conditionalStyles)}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                onMouseEnter={handleMainContainerMouseOver}
                onMouseOver={handleMainContainerMouseOver}
                onMouseLeave={handleMainContainerMouseLeave}
                ref={c => (attachmentDiv.current = c)}
                onClick={performOpenAction}
                onContextMenu={onContextMenu}>
                <Draggable
                    canDrag={canDrag}
                    getDragData={getDragData}
                    getDragPreview={getDragPreview}
                    xOffset={DRAG_X_OFFSET}
                    yOffset={DRAG_Y_OFFSET}>
                    {renderAttachmentView()}
                </Draggable>
            </div>
        </div>
    );
});

function getAttachmentStyle(previewState: AttachmentPreviewMode): string {
    switch (previewState) {
        case AttachmentPreviewMode.Edit:
            return 'edit';
        case AttachmentPreviewMode.NotSelectedAndSaving:
            return 'notSelectedAndSaving';
        case AttachmentPreviewMode.OtherSaving:
            return 'attachmentView';
        case AttachmentPreviewMode.Selected:
            return 'selected';
        case AttachmentPreviewMode.SelectedAndPending:
            return 'selectedAndPending';
        case AttachmentPreviewMode.SelectedAndSaving:
            return 'selectedAndSaving';
        default:
            return 'none';
    }
}

function getIsSelected(previewState: AttachmentPreviewMode): boolean {
    switch (previewState) {
        case AttachmentPreviewMode.Edit:
        case AttachmentPreviewMode.Selected:
        case AttachmentPreviewMode.SelectedAndPending:
        case AttachmentPreviewMode.SelectedAndSaving:
            return true;
        default:
            return false;
    }
}

let attachmentIdShowingOpenAndEditInCloudFRECallout: string = null;
function shouldShowActionsContainerForOpenAndEditInCloudFRECallout(
    attachmentViewState: AttachmentFullViewState
): boolean {
    if (
        isOfficeFile(attachmentViewState.attachmentId) &&
        (isFeatureEnabled('wvn-productizeSTCHoverAndCloudWithTeaching') ||
            isFeatureEnabled('wvn-productizeSTCChicletAndPreview')) &&
        isLightningItemUnseen(LightningId.AttachmentSaveToCloudOpenAndEdit) &&
        !getShouldHideSurfaceActionButton(SurfaceAction.openAndEdit, attachmentViewState)
    ) {
        if (attachmentIdShowingOpenAndEditInCloudFRECallout === null) {
            attachmentIdShowingOpenAndEditInCloudFRECallout = attachmentViewState.attachmentId.Id;
            return true;
        } else if (
            attachmentIdShowingOpenAndEditInCloudFRECallout === attachmentViewState.attachmentId.Id
        ) {
            return true;
        }
    }

    return false;
}
